/**
 * Resource backed by a network API client supporting standard
 * CRUD operations.
 */

import isObject from 'lodash/isObject';
import mapValues from 'lodash/mapValues';

import {Model, ModelResource, ModelStatus} from './model-resource';

export interface ApiResourceClient<ModelValue> {
  index: () => {
    cache: Promise<{[key: string]: ModelValue}>;
    network: Promise<{[key: string]: ModelValue}>;
  };
  get?: (
    id: string,
  ) => {
    cache: Promise<ModelValue>;
    network: Promise<ModelValue>;
  };
  purgeCache: (id?: string) => void;
}

const INDEX_FETCH_ID = `idx` as const;

//** Map where a key of 'idx' holds a different value than other string keys */
interface FetchesInProgressMap<ModelValue> extends Map<'idx' | string, any> {
  get(key: typeof INDEX_FETCH_ID): ReturnType<ApiResourceClient<ModelValue>['index']> | undefined;
  get(key: string): {cache: Promise<ModelValue>; network: Promise<ModelValue>} | undefined;
  set(key: typeof INDEX_FETCH_ID, value: ReturnType<ApiResourceClient<ModelValue>['index']>): this;
  set(key: string, value: {cache: Promise<ModelValue>; network: Promise<ModelValue>}): this;
}

// helper to apply potentially-nested object updates
// without killing other fields
// TODO extract and test
export function applyPartialUpdates(value, update) {
  if (isObject(value) && isObject(update)) {
    for (const [k, v] of Object.entries(update)) {
      value[k] = applyPartialUpdates(value[k], v);
    }
  } else {
    value = update;
  }
  return value;
}

export class ApiResource<ModelValue> extends ModelResource {
  protected apiClient: ApiResourceClient<ModelValue>;
  protected fetchesInProgress: FetchesInProgressMap<ModelValue> = new Map();
  protected models: Map<string, Model<ModelValue>> = null;

  getOne(id: string | number): Model<ModelValue> {
    let model: Model<ModelValue> = null;
    if (this.models === null) {
      this.fetchIndex();
      model = this.loadingModel();
    } else if (this.fetchesInProgress.has(INDEX_FETCH_ID)) {
      model = this.loadingModel();
    } else {
      id = String(id);
      if (this.models.has(id)) {
        model = this.models.get(id);
      } else {
        model = this.notFoundModel();
      }
    }
    return model;
  }

  getAll(): Map<string, Model<ModelValue>> {
    let models;
    if (this.models === null) {
      this.fetchIndex();
      models = new Map();
    } else {
      models = this.models;
    }
    return models;
  }

  getAllValues(): ModelValue[] {
    return Array.from(this.getAll().values()).map((model) => model.value);
  }

  /**
   * Checks if all models are currently loading.
   */
  isLoadingAll(): boolean {
    return this.fetchesInProgress.has(INDEX_FETCH_ID);
  }

  /**
   * Ensures an index fetch has happened: waits for any in-progress index fetch to complete, or
   * starts one if it hasn't been done already
   */
  async waitForIndexFetch(): Promise<void> {
    const fetchInProgress = this.fetchesInProgress.get(INDEX_FETCH_ID);
    if (fetchInProgress) {
      const {cache, network} = fetchInProgress;
      await Promise.all([cache, network]);
    } else {
      if (this.models) {
        // already fetched
        return Promise.resolve();
      } else {
        return this.fetchIndex();
      }
    }
  }

  // TODO support network update or not (configurable by the particular apiClient
  // and/or by caller?)
  update(updates: {[key: string]: Partial<ModelValue>}, {partialUpdate = true}: {partialUpdate?: boolean} = {}) {
    const models = this.models ?? new Map();
    for (const [id, update] of Object.entries(updates)) {
      let currentModel: Model<ModelValue> = models.get(String(id));
      if (!currentModel) {
        currentModel = {value: update as ModelValue, status: ModelStatus.Success};
        models.set(id, currentModel);
      } else {
        currentModel.value = partialUpdate ? applyPartialUpdates(currentModel.value, update) : update;
      }
    }
    this.models = models;
    this.apiClient.purgeCache();
    this.notifySubscribers();
  }

  private setModels(modelValues, notify: boolean) {
    if (!modelValues) {
      return;
    }

    this.models = new Map(
      Object.entries(mapValues(modelValues, (value): Model<ModelValue> => ({value, status: ModelStatus.Success}))),
    );
    this.fetchesInProgress.delete(INDEX_FETCH_ID);

    // avoid superfluous repeated notifications by only
    // notifying in the call where we actually made the
    // API call or looked in browser cache
    if (notify) {
      this.notifySubscribers();
    }
  }

  private setOneModel(id, value, notify: boolean) {
    // TODO do we want to support null values? If so, fromFetchCache stuff
    // in SharedClient needs reworking
    if (value === null) {
      return;
    }

    this.models = this.models ?? new Map();
    const model: Model<ModelValue> =
      typeof value === `undefined` ? this.notFoundModel() : {value, status: ModelStatus.Success};
    this.models.set(id, model);
    this.fetchesInProgress.delete(id);

    // avoid superfluous repeated notifications by only
    // notifying in the call where we actually made the
    // API call or looked in browser cache
    if (notify) {
      this.notifySubscribers();
    }
  }

  private async fetchIndex() {
    let initiatedFetch = false;
    if (!this.fetchesInProgress.has(INDEX_FETCH_ID)) {
      this.fetchesInProgress.set(INDEX_FETCH_ID, this.apiClient.index());
      initiatedFetch = true;

      // allow components to render loading state
      this.notifySubscribers();
    }

    const {cache, network} = this.fetchesInProgress.get(INDEX_FETCH_ID);

    await Promise.all([
      cache.then((modelValues) => this.setModels(modelValues, initiatedFetch)),
      network.then((modelValues) => this.setModels(modelValues, initiatedFetch)),
    ]);
  }

  protected async fetchOne(id: string) {
    let initiatedFetch = false;

    if (!this.fetchesInProgress.has(id)) {
      this.fetchesInProgress.set(id, this.apiClient.get(id));
      initiatedFetch = true;
    }

    const {cache, network} = this.fetchesInProgress.get(id);
    cache.then((modelValue) => this.setOneModel(id, modelValue, initiatedFetch));
    network.then((modelValue) => this.setOneModel(id, modelValue, initiatedFetch));

    await Promise.all([cache, network]);
    this.fetchesInProgress.delete(id);
  }

  protected loadingModel(): Model<ModelValue> {
    return {status: ModelStatus.Loading};
  }

  protected notFoundModel(): Model<ModelValue> {
    return {status: ModelStatus.NotFound};
  }
}

export class ApiSingleValueResource<ModelValue> extends ApiResource<ModelValue> {
  public id: string = null;

  get modelKey() {
    return String(this.id);
  }

  get(): Model<ModelValue> {
    let model = null;
    if (this.models === null || !this.models.has(this.modelKey)) {
      this.fetchOne(this.modelKey);
      model = this.loadingModel();
    } else if (this.fetchesInProgress.has(this.modelKey)) {
      model = this.loadingModel();
    } else {
      model = this.models.get(this.modelKey);
    }
    return model;
  }

  // TODO support network update or not (configurable by the particular apiClient
  // and/or by caller?)
  update(update) {
    const models = this.models || new Map();
    let currentModel: Model<ModelValue> = models.get(this.modelKey);
    if (!currentModel) {
      currentModel = {value: update, status: ModelStatus.Success};
      models.set(this.modelKey, currentModel);
    } else {
      currentModel.value = applyPartialUpdates(currentModel.value, update);
    }
    this.models = models;
    this.apiClient.purgeCache(this.modelKey);
    this.notifySubscribers();
  }
}
